import { useEffect, useMemo, useState } from 'react';
import { BookOpen, X, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { api, type Faq } from '../lib/api';

interface Props {
  tenantCode: string;
  onPaste: (answer: string) => void;
  onClose: () => void;
}

export function FaqPanel({ tenantCode, onPaste, onClose }: Props) {
  const [items, setItems] = useState<Faq[]>([]);
  const [categories, setCategories] = useState<Array<{ slug: string; label: string; count: number }>>([]);
  const [filter, setFilter] = useState('');
  const [selectedCat, setSelectedCat] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .listFaqs(tenantCode)
      .then((r) => {
        setItems(r.items);
        setCategories(r.categories);
      })
      .catch(() => {
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [tenantCode]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return items.filter((f) => {
      if (selectedCat && f.category !== selectedCat) return false;
      if (!q) return true;
      return (
        (f.question ?? '').toLowerCase().includes(q) ||
        (f.answer ?? '').toLowerCase().includes(q)
      );
    });
  }, [items, filter, selectedCat]);

  function toggle(id: string) {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="absolute bottom-full left-0 mb-1 bg-white border border-gray-200 rounded-md shadow-lg w-[420px] max-h-96 overflow-hidden flex flex-col z-20">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
        <span className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
          <BookOpen className="w-3.5 h-3.5" /> FAQ
        </span>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-2 border-b border-gray-100 flex gap-1.5">
        <input
          type="text"
          autoFocus
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter FAQs…"
          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <select
          value={selectedCat ?? ''}
          onChange={(e) => setSelectedCat(e.target.value || null)}
          className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">All ({items.length})</option>
          {categories.map((c) => (
            <option key={c.slug} value={c.slug}>
              {c.label} ({c.count})
            </option>
          ))}
        </select>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-xs text-gray-500 flex items-center justify-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-center text-xs text-gray-500">No FAQs match.</div>
        ) : (
          filtered.map((f) => {
            const isOpen = expanded.has(f.id);
            return (
              <div key={f.id} className="border-b border-gray-100 last:border-0">
                <button
                  type="button"
                  onClick={() => toggle(f.id)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-start gap-1.5"
                >
                  {isOpen ? (
                    <ChevronDown className="w-3.5 h-3.5 mt-0.5 text-gray-400 shrink-0" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5 mt-0.5 text-gray-400 shrink-0" />
                  )}
                  <div className="flex-1">
                    <div className="text-xs font-medium text-gray-900" dir="auto">{f.question}</div>
                    {f.categoryLabel && (
                      <div className="text-[10px] text-gray-500 mt-0.5">{f.categoryLabel}</div>
                    )}
                  </div>
                </button>
                {isOpen && (
                  <div className="px-3 pb-2 pl-7">
                    <div className="text-[12px] text-gray-700 whitespace-pre-wrap mb-1.5" dir="auto">
                      {f.answer}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        onPaste(f.answer);
                        onClose();
                      }}
                      className="text-[11px] px-2 py-0.5 bg-primary-600 text-white rounded hover:bg-primary-700"
                    >
                      Paste into reply
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
